import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  addToast,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure
} from "@heroui/react";
import {
  BAFTokenStakedListApi,
  BAFTokenUnStakeApi,
  writeContractApi
} from "../../../../api/methods-marketplace";
import bafStakingABI from "../../../../abis/baf-token-staking-abi.json";
import { useWallet } from "../../../../context/WalletContext";
import { useSelector } from "react-redux";
import { getRemainingDays, isPlanCompleted } from "../../../../utils/common";
import { formatUnits, encodeFunctionData } from "viem";
import { waitForTransactionReceipt, writeContract } from "@wagmi/core";
import { rainbowKitConfig } from "../../../../utils/config";

const BAFStakingTable = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stakeTokenList, setStakeTokenList] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [initEmergencyWithdrawLoading, setInitEmergencyWithdrawLoading] =
    useState(false);

  const {
    isConnected,
    address,
    hasAddressMismatch,
    refreshBalance,
    isCustodialWallet,
    publicClient
  } = useWallet();
  const user = useSelector((state) => state.user);
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();

  // Constants for contract addresses
  const BAF_STAKING_CONTRACT_ADDRESS =
    process.env.REACT_APP_BAF_STAKING_CONTRACT_ADDRESS;

  const tableColumns = [
    { uid: "staking_plan_id", label: "Plans" },
    { uid: "duration", label: "Time Remaining" },
    { uid: "amount", label: "Staked Balance" },
    { uid: "reward", label: "Accrued Reward" },
    { uid: "action", label: "Action" }
  ];

  const classNames = useMemo(
    () => ({
      base: ["p-0"],
      th: [
        "bg-transparent",
        "text-black",
        "border-b",
        "border-[#C2C2C2]",
        "py-4",
        "px-4",
        "whitespace-nowrap",
        "text-lg",
        "font-normal",
        "font-bold"
      ],
      td: [
        "py-3",
        "px-4",
        "group-data-[first=true]/tr:first:before:rounded-none",
        "group-data-[first=true]/tr:last:before:rounded-none",
        "group-data-[middle=true]/tr:before:rounded-none",
        "group-data-[last=true]/tr:first:before:rounded-none",
        "group-data-[last=true]/tr:last:before:rounded-none"
      ]
    }),
    []
  );

  const fetchRewardForStaking = useCallback(
    async (planId, userAddress) => {
      if (!publicClient) return "Error: public client not found";

      try {
        const reward = await publicClient.readContract({
          address: BAF_STAKING_CONTRACT_ADDRESS,
          abi: bafStakingABI,
          functionName: "calculateDailyReward",
          args: [userAddress, planId]
        });

        return formatUnits(reward, 18);
      } catch (error) {
        console.log("🚀 ~ fetchRewardForStaking ~ error:", error);
        return "Error";
      }
    },
    [publicClient, BAF_STAKING_CONTRACT_ADDRESS]
  );

  const fetchStakedTokenPlanList = useCallback(async () => {
    try {
      const response = await BAFTokenStakedListApi();
      if (response?.data?.data) {
        const data = response?.data?.data;

        const enrichedData = await Promise.all(
          data.map(async (plan) => {
            const reward = await fetchRewardForStaking(
              plan.staking_plan_id,
              address
            );
            return { ...plan, reward, isLoading: false, isDisabled: false };
          })
        );

        setStakeTokenList(enrichedData);
      }
    } catch (error) {
      console.log("🚀 ~ fetchStakedTokenPlanList ~ error:", error);
      addToast({
        title: "Error",
        description: "Failed to fetch staked token list. Please try again.",
        color: "danger",
        variant: "bordered"
      });
    } finally {
      setIsLoading(false);
    }
  }, [address, fetchRewardForStaking]);

  const resetAllState = useCallback(() => {
    setStakeTokenList((prev) =>
      prev.map((item) => ({
        ...item,
        isLoading: false,
        isDisabled: false
      }))
    );
  }, []);

  const handleClosePopup = useCallback(() => {
    setInitEmergencyWithdrawLoading(false);
    onClose();
    resetAllState();
  }, [onClose, resetAllState]);

  const handleBAFTokenUnStake = useCallback(
    async (data) => {
      try {
        const response = await BAFTokenUnStakeApi(data);
        if (response) {
          await fetchStakedTokenPlanList();
          handleClosePopup();
        }
      } catch (error) {
        console.error(
          "Error during BAF token unstaking:",
          error?.message || error
        );
      }
    },
    [fetchStakedTokenPlanList, handleClosePopup]
  );

  const initWithdrawProcess = useCallback(
    async (planId) => {
      if (!isCustodialWallet) {
        if (!isConnected || !address) {
          addToast({
            title: "Not Connected",
            description: "Please connect your wallet first.",
            color: "danger",
            variant: "bordered"
          });
          return;
        } else if (hasAddressMismatch) {
          addToast({
            title: "Address Mismatch",
            description:
              "The connected wallet address does not match your registered address.",
            color: "danger",
            variant: "bordered"
          });
          return;
        }
      }

      setStakeTokenList((prev) =>
        prev.map((item) => ({
          ...item,
          isLoading: item.staking_plan_id === planId,
          isDisabled: true
        }))
      );

      try {
        if (isCustodialWallet) {
          if (!publicClient) {
            throw new Error("publicClient is required for custodial wallet");
          }

          const withdrawCallData = encodeFunctionData({
            abi: bafStakingABI,
            functionName: "withdraw",
            args: [planId]
          });

          const withdrawResult = await writeContractApi({
            address: BAF_STAKING_CONTRACT_ADDRESS,
            encoded_data: withdrawCallData
          });

          if (!withdrawResult?.data?.tx_hash) {
            throw new Error("Transaction hash not received");
          }

          const txHash = withdrawResult.data.tx_hash;
          console.log("Withdraw tx hash:", txHash);

          // Wait for confirmation
          const receipt = await publicClient.waitForTransactionReceipt({
            hash: txHash
          });

          console.log("Withdraw receipt:", receipt);

          if (receipt.status === "success") {
            addToast({
              title: "Unstaking Successful",
              description: "Your tokens have been successfully unstaked.",
              color: "success",
              variant: "bordered"
            });

            await handleBAFTokenUnStake({ plan: planId, txHash });
            await refreshBalance();
          } else {
            addToast({
              title: "Unstaking Failed",
              description:
                "The transaction was unsuccessful. Please try again.",
              color: "danger",
              variant: "bordered"
            });
          }
        } else {
          const txHash = await writeContract(rainbowKitConfig, {
            account: address,
            abi: bafStakingABI,
            address: BAF_STAKING_CONTRACT_ADDRESS,
            functionName: "withdraw",
            args: [planId]
          });

          const receipt = await waitForTransactionReceipt(rainbowKitConfig, {
            hash: txHash,
            confirmations: 1
          });

          if (receipt.status === "success") {
            addToast({
              title: "Unstaking Successful",
              description: "Your tokens have been successfully unstaked.",
              color: "success",
              variant: "bordered"
            });

            await handleBAFTokenUnStake({ plan: planId, txHash: receipt.hash });
            await refreshBalance();
          } else {
            addToast({
              title: "Unstaking Failed",
              description:
                "The transaction was unsuccessful. Please try again.",
              color: "danger",
              variant: "bordered"
            });
          }
        }
      } catch (error) {
        console.error("Withdraw error:", error);

        const isUserRejected =
          error?.message?.includes("User rejected") ||
          error?.message?.includes("User denied");

        addToast({
          title: isUserRejected ? "Transaction Cancelled" : "Withdraw Failed",
          description: isUserRejected
            ? "You cancelled the transaction in your wallet."
            : error?.response?.data?.message ||
              error?.message ||
              "Something went wrong with the withdrawal. Please try again or check your wallet.",
          color: isUserRejected ? "warning" : "danger",
          variant: "bordered"
        });
      } finally {
        resetAllState();
      }
    },
    [
      isConnected,
      address,
      hasAddressMismatch,
      BAF_STAKING_CONTRACT_ADDRESS,
      handleBAFTokenUnStake,
      resetAllState,
      isCustodialWallet,
      publicClient,
      refreshBalance
    ]
  );

  const initEmergencyWithdrawProcess = useCallback(
    async (planId) => {
      if (!isCustodialWallet) {
        if (!isConnected || !address || hasAddressMismatch) {
          addToast({
            title: "Not Connected",
            description: "Please connect your wallet first.",
            color: "danger",
            variant: "bordered"
          });
          return;
        }
      }

      setInitEmergencyWithdrawLoading(true);

      try {
        setStakeTokenList((prev) =>
          prev.map((item) => ({
            ...item,
            isLoading: item.staking_plan_id === planId,
            isDisabled: true
          }))
        );

        if (isCustodialWallet) {
          if (!publicClient) {
            throw new Error("publicClient is required for custodial wallet");
          }

          const emergencyWithdrawCallData = encodeFunctionData({
            abi: bafStakingABI,
            functionName: "emergencyWithdraw",
            args: [planId]
          });

          const emergencyResult = await writeContractApi({
            address: BAF_STAKING_CONTRACT_ADDRESS,
            encoded_data: emergencyWithdrawCallData
          });

          if (!emergencyResult?.data?.tx_hash) {
            throw new Error("Transaction hash not received");
          }

          const txHash = emergencyResult.data.tx_hash;
          console.log("Emergency withdraw tx hash:", txHash);

          // Wait for transaction confirmation
          const receipt = await publicClient.waitForTransactionReceipt({
            hash: txHash
          });

          console.log("Emergency withdraw receipt:", receipt);

          if (receipt.status === "success") {
            addToast({
              title: "Emergency Unstaking Successful",
              description:
                "Your tokens have been successfully unstaked via emergency withdrawal.",
              color: "success",
              variant: "bordered"
            });

            await handleBAFTokenUnStake({ plan: planId, txHash });
            await refreshBalance();
          } else {
            addToast({
              title: "Emergency Unstaking Failed",
              description:
                "The transaction was unsuccessful. Please try again.",
              color: "danger",
              variant: "bordered"
            });
          }
        } else {
          const txHash = await writeContract(rainbowKitConfig, {
            account: address,
            abi: bafStakingABI,
            address: BAF_STAKING_CONTRACT_ADDRESS,
            functionName: "emergencyWithdraw",
            args: [planId]
          });

          const receipt = await waitForTransactionReceipt(rainbowKitConfig, {
            hash: txHash,
            confirmations: 1
          });

          if (receipt.status === "success") {
            addToast({
              title: "Emergency Unstaking Successful",
              description:
                "Your tokens have been successfully unstaked via emergency withdrawal.",
              color: "success",
              variant: "bordered"
            });

            await handleBAFTokenUnStake({
              plan: planId,
              txHash: receipt?.hash
            });
            await refreshBalance();
          } else {
            addToast({
              title: "Emergency Unstaking Failed",
              description:
                "The transaction was unsuccessful. Please try again.",
              color: "danger",
              variant: "bordered"
            });
          }
        }
      } catch (error) {
        console.error("Emergency withdraw error:", error);

        const isUserRejected =
          error?.message?.includes("User rejected") ||
          error?.message?.includes("User denied");

        addToast({
          title: isUserRejected
            ? "Transaction Cancelled"
            : "Emergency Withdraw Failed",
          description: isUserRejected
            ? "You cancelled the transaction in your wallet."
            : error?.response?.data?.message ||
              error?.message ||
              "Something went wrong with the emergency withdrawal. Please try again or check your wallet for more details.",
          color: isUserRejected ? "warning" : "danger",
          variant: "bordered"
        });
      } finally {
        setInitEmergencyWithdrawLoading(false);
        resetAllState();
      }
    },
    [
      isConnected,
      address,
      hasAddressMismatch,
      BAF_STAKING_CONTRACT_ADDRESS,
      handleBAFTokenUnStake,
      resetAllState,
      isCustodialWallet,
      publicClient,
      refreshBalance
    ]
  );

  const handleUnStake = useCallback(
    (data) => {
      if (!isCustodialWallet) {
        if (!address || !isConnected) {
          addToast({
            title: "Not Connected",
            description: "Please connect your wallet first.",
            color: "danger",
            variant: "bordered"
          });
          return;
        } else if (hasAddressMismatch) {
          addToast({
            title: "Address Mismatch",
            description:
              "The connected wallet address does not match your registered address.",
            color: "danger",
            variant: "bordered"
          });
          return;
        }
      }

      const remainingDays = isPlanCompleted(
        data?.created_at,
        data?.number_of_days
      );

      if (!remainingDays) {
        onOpen();
        setSelectedPlan(data?.staking_plan_id);
      } else {
        initWithdrawProcess(data?.staking_plan_id);
      }
    },
    [
      address,
      isConnected,
      hasAddressMismatch,
      onOpen,
      initWithdrawProcess,
      isCustodialWallet
    ]
  );

  const renderCell = useCallback(
    (data, columnKey) => {
      const cellValue = data[columnKey];

      switch (columnKey) {
        case "staking_plan_id":
          return (
            <div className="flex flex-col">
              <p className="font-sans text-lg text-black font-semibold">
                Plan {cellValue}
              </p>
            </div>
          );
        case "duration":
          return (
            <div className="flex flex-col">
              <p className="font-sans text-lg text-black font-normal">
                {getRemainingDays(data.created_at, data.number_of_days)}
              </p>
            </div>
          );
        case "amount":
          return (
            <div className="flex flex-col">
              <span className="font-sans text-lg text-black font-normal">
                {cellValue} BAF
              </span>
            </div>
          );
        case "reward":
          return (
            <div className="flex flex-col">
              <span className="font-sans text-lg text-black font-normal">
                {cellValue}
              </span>
            </div>
          );
        case "action":
          return (
            <div className="relative flex justify-end items-center gap-2">
              {isPlanCompleted(data?.created_at, data?.number_of_days) ? (
                <Button
                  className="bg-yellowbuy border border-black text-base font-bold h-10 min-w-28"
                  radius="sm"
                  onPress={() => handleUnStake(data)}
                  isDisabled={data?.isDisabled}
                  isLoading={data?.isLoading}
                >
                  UnStake
                </Button>
              ) : (
                <Button
                  className="bg-yellowbuy border border-black text-base font-bold h-10 min-w-28"
                  radius="sm"
                  isDisabled={data?.isDisabled}
                  isLoading={data?.isLoading}
                  onPress={() => handleUnStake(data)}
                >
                  Emergency withdraw
                </Button>
              )}
            </div>
          );
        default:
          return cellValue;
      }
    },
    [handleUnStake]
  );

  useEffect(() => {
    let mounted = true;

    const initializeData = async () => {
      if (!user?.login) {
        setIsLoading(false);
        return;
      }

      if (address === null || address === false) {
        setIsLoading(false);
        return;
      }

      if (address && typeof address === "string") {
        if (isCustodialWallet || isConnected) {
          if (mounted) {
            await fetchStakedTokenPlanList();
          }
        }
      } else if (address === undefined) {
        return;
      } else {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(initializeData, 100);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [address, user, isConnected, isCustodialWallet, fetchStakedTokenPlanList]);

  return (
    <>
      <div className="z-0 flex flex-col relative justify-between gap-4 bg-content1 overflow-auto rounded-large w-full border border-[#C2C2C2]">
        <Table
          aria-label="Staking plans table"
          removeWrapper
          classNames={classNames}
        >
          <TableHeader columns={tableColumns}>
            {(column) => (
              <TableColumn
                key={column.uid}
                align={column.uid === "action" ? "end" : "start"}
              >
                {column.label}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody
            isLoading={isLoading}
            items={stakeTokenList}
            emptyContent={"You don't have any active staking plans."}
            loadingContent={
              <Spinner
                classNames={{
                  circle1: "!border-b-yellowbuy",
                  circle2: "!border-b-yellowbuy"
                }}
                label="Loading..."
              />
            }
          >
            {(item) => (
              <TableRow key={item?.id || item?.staking_plan_id}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Modal
        size="sm"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        classNames={{
          closeButton: "hidden"
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Are you sure?
              </ModalHeader>
              <ModalBody>
                <p className="m-0 text-sm text-black">
                  Unstaking before contract maturity will result in a 10%
                  penalty on rewards. Are you sure?
                </p>
              </ModalBody>
              <ModalFooter className="justify-center">
                <Button
                  onPress={handleClosePopup}
                  className="bg-yellowbuy border border-black rounded-lg text-sm font-normal text-black h-8"
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  isLoading={initEmergencyWithdrawLoading}
                  disabled={initEmergencyWithdrawLoading}
                  onPress={() => initEmergencyWithdrawProcess(selectedPlan)}
                  className="bg-white border border-black rounded-lg text-sm font-normal text-black h-8"
                >
                  Confirm
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default BAFStakingTable;
